import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'

import { Button, type ButtonProps } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export type StateSurfaceVariant = 'fullscreen' | 'section' | 'inline'
export type StateSurfaceTone = 'default' | 'error' | 'warning' | 'success' | 'info'
export type StateSurfaceSize = 'sm' | 'md' | 'lg'

export interface StateSurfaceAction {
  label: ReactNode
  to?: string
  onClick?: () => void
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  icon?: ReactNode
  className?: string
}

export interface StateSurfaceProps {
  asset?: ReactNode
  assetAlt?: string
  eyebrow?: ReactNode
  title?: ReactNode
  description?: ReactNode
  primaryAction?: StateSurfaceAction
  secondaryAction?: StateSurfaceAction
  variant?: StateSurfaceVariant
  tone?: StateSurfaceTone
  size?: StateSurfaceSize
  className?: string
  contentClassName?: string
}

const containerClasses: Record<StateSurfaceVariant, string> = {
  fullscreen:
    'relative isolate flex min-h-screen overflow-hidden bg-background px-6 py-10 sm:px-8 sm:py-14',
  section:
    'relative isolate overflow-hidden rounded-xl border border-dashed border-border bg-card/60 px-5 py-8 sm:px-8',
  inline:
    'relative isolate overflow-hidden rounded-lg border border-dashed border-border/70 bg-background/60 px-4 py-5',
}

const contentClasses: Record<StateSurfaceVariant, string> = {
  fullscreen: 'relative z-10 mx-auto flex w-full max-w-3xl flex-1 flex-col items-center justify-center text-center',
  section: 'relative z-10 mx-auto flex w-full max-w-2xl flex-col items-center text-center',
  inline: 'relative z-10 mx-auto flex w-full max-w-xl flex-col items-center text-center',
}

const spacingClasses: Record<StateSurfaceSize, string> = {
  sm: 'gap-3',
  md: 'gap-4',
  lg: 'gap-5',
}

const titleClasses: Record<StateSurfaceSize, string> = {
  sm: 'text-lg font-semibold tracking-tight',
  md: 'text-2xl font-semibold tracking-tight',
  lg: 'text-3xl font-semibold tracking-tight md:text-4xl',
}

const descriptionClasses: Record<StateSurfaceSize, string> = {
  sm: 'max-w-lg text-sm leading-6 text-muted-foreground',
  md: 'max-w-xl text-sm leading-7 text-muted-foreground md:text-base',
  lg: 'max-w-2xl text-sm leading-7 text-muted-foreground md:text-base',
}

const eyebrowClasses: Record<StateSurfaceTone, string> = {
  default: 'text-muted-foreground',
  error: 'text-rose-700 dark:text-rose-300',
  warning: 'text-amber-700 dark:text-amber-300',
  success: 'text-emerald-700 dark:text-emerald-300',
  info: 'text-sky-700 dark:text-sky-300',
}

const assetSizeClasses: Record<StateSurfaceSize, string> = {
  sm: 'h-32 w-32',
  md: 'h-44 w-44',
  lg: 'h-56 w-56 md:h-64 md:w-64',
}

function renderAction(action: StateSurfaceAction, fallbackVariant: ButtonProps['variant']) {
  const variant = action.variant ?? fallbackVariant
  const size = action.size ?? 'lg'
  const content = (
    <>
      {action.icon}
      <span>{action.label}</span>
    </>
  )

  if (action.to) {
    return (
      <Button asChild size={size} variant={variant} className={cn('min-w-[140px] h-10 rounded-[8px]', action.className)}>
        <Link to={action.to}>{content}</Link>
      </Button>
    )
  }

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      className={cn('min-w-[140px] h-10 rounded-[8px]', action.className)}
      onClick={action.onClick}
    >
      {content}
    </Button>
  )
}

export function BrandIconMark({
  size = 'md',
  className,
}: {
  size?: StateSurfaceSize
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div className={cn('flex items-center justify-center', className)}>
      <img
        src="/Uploads/logo-mark-light.svg"
        alt={t('app.brandName')}
        className={cn('dark:hidden', assetSizeClasses[size])}
      />
      <img
        src="/Uploads/logo-mark-dark.svg"
        alt={t('app.brandName')}
        className={cn('hidden dark:block', assetSizeClasses[size])}
      />
    </div>
  )
}

export function BrandLoadingMark({
  size = 'md',
  className,
}: {
  size?: StateSurfaceSize
  className?: string
}) {
  return (
    <BrandIconMark
      size={size}
      className={cn('state-surface-brand-mark state-surface-brand-mark--animated', className)}
    />
  )
}

export function PlaceholderStateAsset({
  size = 'md',
  className,
}: {
  size?: StateSurfaceSize
  className?: string
}) {
  return (
    <div className={cn('relative isolate', assetSizeClasses[size], className)} aria-hidden="true">
      <div className="absolute inset-0 rounded-[28%] bg-[radial-gradient(circle_at_30%_25%,rgba(255,255,255,0.98),rgba(255,255,255,0.55)_35%,rgba(181,194,255,0.14)_70%,transparent_100%)]" />
      <div className="absolute left-[18%] top-[18%] h-[54%] w-[52%] rotate-[-18deg] rounded-[18%] border border-white/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(240,244,255,0.9))] shadow-[0_18px_50px_rgba(84,112,255,0.14)]" />
      <div className="absolute right-[14%] top-[10%] h-[64%] w-[56%] rotate-[4deg] rounded-[18%] border border-indigo-100/80 bg-[linear-gradient(160deg,rgba(224,230,255,0.96),rgba(121,142,255,0.88))] shadow-[0_26px_60px_rgba(77,101,255,0.18)]" />
      <div className="absolute right-[20%] top-[14%] h-[20%] w-[4%] rotate-[10deg] rounded-full border border-white/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.92),rgba(210,219,255,0.88))]" />
      <div className="absolute left-[24%] top-[52%] h-[18%] w-[18%] rounded-full bg-[radial-gradient(circle_at_35%_35%,rgba(255,255,255,0.92),rgba(184,196,255,0.95)_62%,rgba(123,141,255,0.98))] shadow-[0_20px_45px_rgba(73,97,255,0.22)]" />
      <div className="absolute left-[58%] top-[44%] h-[26%] w-[22%] rotate-[12deg] rounded-full border border-white/60 bg-[linear-gradient(180deg,rgba(246,248,255,0.96),rgba(191,203,255,0.95))] shadow-[0_14px_30px_rgba(72,96,255,0.18)]" />
      <div className="absolute bottom-[18%] left-[8%] h-[14%] w-[34%] rotate-[-28deg] rounded-full bg-[linear-gradient(90deg,rgba(114,137,255,0.96),rgba(72,102,255,0.88))] shadow-[0_16px_36px_rgba(72,102,255,0.2)]" />
      <div className="absolute bottom-[15%] left-[18%] h-[9%] w-[9%] rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.95),rgba(188,200,255,0.9)_60%,rgba(128,145,255,0.9))]" />
    </div>
  )
}

export function StateSurfaceIconAsset({
  icon: Icon,
  size = 'md',
  className,
}: {
  icon: LucideIcon
  size?: StateSurfaceSize
  className?: string
}) {
  const tileClasses: Record<StateSurfaceSize, string> = {
    sm: 'size-10',
    md: 'size-14',
    lg: 'size-16',
  }

  const iconClasses: Record<StateSurfaceSize, string> = {
    sm: 'size-5',
    md: 'size-7',
    lg: 'size-8',
  }

  return (
    <div
      className={cn(
        'inline-flex items-center justify-center rounded-[var(--radius)] bg-muted text-muted-foreground',
        tileClasses[size],
        className,
      )}
      aria-hidden="true"
    >
      <Icon className={iconClasses[size]} strokeWidth={1.75} />
    </div>
  )
}

export function StateSurface({
  asset,
  eyebrow,
  title,
  description,
  primaryAction,
  secondaryAction,
  variant = 'section',
  tone = 'default',
  size = 'md',
  className,
  contentClassName,
}: StateSurfaceProps) {
  return (
    <section className={cn(containerClasses[variant], className)}>
      <div className={cn(contentClasses[variant], spacingClasses[size], contentClassName)}>
        {asset ? <div className="flex items-center justify-center">{asset}</div> : null}

        {eyebrow ? (
          <p className={cn('text-xs font-semibold uppercase tracking-[0.24em]', eyebrowClasses[tone])}>
            {eyebrow}
          </p>
        ) : null}

        {title ? <h1 className={cn('text-balance text-foreground', titleClasses[size])}>{title}</h1> : null}

        {description ? (
          typeof description === 'string' || typeof description === 'number' ? (
            <p className={cn('text-balance', descriptionClasses[size])}>{description}</p>
          ) : (
            <div className={cn('text-balance', descriptionClasses[size])}>{description}</div>
          )
        ) : null}

        {primaryAction || secondaryAction ? (
          <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
            {primaryAction ? renderAction(primaryAction, 'default') : null}
            {secondaryAction ? renderAction(secondaryAction, 'outline') : null}
          </div>
        ) : null}
      </div>
    </section>
  )
}

export function StateSurfaceSkeleton({
  variant = 'fullscreen',
  size = 'md',
  className,
  contentClassName,
}: {
  variant?: StateSurfaceVariant
  size?: StateSurfaceSize
  className?: string
  contentClassName?: string
}) {
  const assetSkeletonClasses: Record<StateSurfaceSize, string> = {
    sm: 'h-28 w-28 rounded-[28px]',
    md: 'h-40 w-40 rounded-[36px]',
    lg: 'h-52 w-52 rounded-[44px] md:h-60 md:w-60',
  }

  const titleSkeletonClasses: Record<StateSurfaceSize, string> = {
    sm: 'h-7 w-56',
    md: 'h-9 w-72',
    lg: 'h-11 w-80 md:w-[28rem]',
  }

  const lineSkeletonClasses: Record<StateSurfaceSize, string> = {
    sm: 'h-4 w-64',
    md: 'h-4 w-80',
    lg: 'h-4 w-[26rem] md:w-[30rem]',
  }

  return (
    <section className={cn(containerClasses[variant], className)}>
      <div className={cn(contentClasses[variant], spacingClasses[size], contentClassName)}>
        <div className="flex items-center justify-center">
          <Skeleton className={cn('shadow-none', assetSkeletonClasses[size])} />
        </div>

        <Skeleton className={cn('h-10 w-40 rounded-full', variant === 'fullscreen' ? 'mt-1' : '')} />
        <Skeleton className={cn('rounded-full', titleSkeletonClasses[size])} />

        <div className="flex flex-col items-center gap-2 pt-1">
          <Skeleton className={cn('rounded-full', lineSkeletonClasses[size])} />
          <Skeleton className={cn('h-4 rounded-full', size === 'lg' ? 'w-80 md:w-[26rem]' : 'w-64')} />
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
          <Skeleton className="h-11 w-40 rounded-xl" />
          <Skeleton className="hidden h-11 w-36 rounded-xl sm:block" />
        </div>
      </div>
    </section>
  )
}
